// src/ScreenTimeAnalytics.js

import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import NavBar from './NavBar';
import { FaArrowLeft, FaClock, FaUsers } from 'react-icons/fa';
import './ScreenTimeAnalyticsPage.css';
import { getAllSBUs, getAllTracks, getGroupMapping, GROUP_MAPPING } from './groupMapping';

const ScreenTimeAnalytics = ({ userName, userEmail, onLogout }) => {
  // Load saved filters from localStorage
  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem('screenTimeFilters');
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Error loading saved filters:', err);
      return {};
    }
  };

  const savedFilters = loadSavedFilters();

  // Assignment Group filters
  const [selectedGroupMonth, setSelectedGroupMonth] = useState(savedFilters.selectedGroupMonth || 'January');
  const [selectedGroupWeek, setSelectedGroupWeek] = useState(savedFilters.selectedGroupWeek || '1');
  const [selectedGroupFromDate, setSelectedGroupFromDate] = useState(savedFilters.selectedGroupFromDate || '2026-01-01');
  const [selectedGroupToDate, setSelectedGroupToDate] = useState(savedFilters.selectedGroupToDate || '2026-01-09');
  const [selectedGroupYear, setSelectedGroupYear] = useState(savedFilters.selectedGroupYear || '2026');
  const [selectedGroup, setSelectedGroup] = useState(savedFilters.selectedGroup || null);
  const [groupFilterType, setGroupFilterType] = useState(savedFilters.groupFilterType || 'month');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  
  // Team filters
  const [selectedTeamMonth, setSelectedTeamMonth] = useState(savedFilters.selectedTeamMonth || 'January');
  const [selectedTeamWeek, setSelectedTeamWeek] = useState(savedFilters.selectedTeamWeek || '1');
  const [selectedTeamFromDate, setSelectedTeamFromDate] = useState(savedFilters.selectedTeamFromDate || '2026-01-01');
  const [selectedTeamToDate, setSelectedTeamToDate] = useState(savedFilters.selectedTeamToDate || '2026-01-09');
  const [selectedTeamYear, setSelectedTeamYear] = useState(savedFilters.selectedTeamYear || '2026');
  const [teamFilterType, setTeamFilterType] = useState(savedFilters.teamFilterType || 'month');

  // All Teams Combined filters
  const [selectedAllTeamsMonth, setSelectedAllTeamsMonth] = useState(savedFilters.selectedAllTeamsMonth || 'January');
  const [selectedAllTeamsWeek, setSelectedAllTeamsWeek] = useState(savedFilters.selectedAllTeamsWeek || '1');
  const [selectedAllTeamsFromDate, setSelectedAllTeamsFromDate] = useState(savedFilters.selectedAllTeamsFromDate || '2026-01-01');
  const [selectedAllTeamsToDate, setSelectedAllTeamsToDate] = useState(savedFilters.selectedAllTeamsToDate || '2026-01-09');
  const [selectedAllTeamsYear, setSelectedAllTeamsYear] = useState(savedFilters.selectedAllTeamsYear || '2026');
  const [allTeamsFilterType, setAllTeamsFilterType] = useState(savedFilters.allTeamsFilterType || 'month');

  // Positive Feedback filters
  const [selectedPositiveFeedbackMonth, setSelectedPositiveFeedbackMonth] = useState(savedFilters.selectedPositiveFeedbackMonth || 'January');
  const [selectedPositiveFeedbackWeek, setSelectedPositiveFeedbackWeek] = useState(savedFilters.selectedPositiveFeedbackWeek || '1');
  const [selectedPositiveFeedbackFromDate, setSelectedPositiveFeedbackFromDate] = useState(savedFilters.selectedPositiveFeedbackFromDate || '2026-01-01');
  const [selectedPositiveFeedbackToDate, setSelectedPositiveFeedbackToDate] = useState(savedFilters.selectedPositiveFeedbackToDate || '2026-01-09');
  const [selectedPositiveFeedbackYear, setSelectedPositiveFeedbackYear] = useState(savedFilters.selectedPositiveFeedbackYear || '2026');
  const [selectedPositiveFeedbackGroup, setSelectedPositiveFeedbackGroup] = useState(savedFilters.selectedPositiveFeedbackGroup || null);
  const [positiveFeedbackFilterType, setPositiveFeedbackFilterType] = useState(savedFilters.positiveFeedbackFilterType || 'month');
  const [showPositiveFeedbackGroupDropdown, setShowPositiveFeedbackGroupDropdown] = useState(false);

  // Negative Feedback filters
  const [selectedNegativeFeedbackMonth, setSelectedNegativeFeedbackMonth] = useState(savedFilters.selectedNegativeFeedbackMonth || 'January');
  const [selectedNegativeFeedbackWeek, setSelectedNegativeFeedbackWeek] = useState(savedFilters.selectedNegativeFeedbackWeek || '1');
  const [selectedNegativeFeedbackFromDate, setSelectedNegativeFeedbackFromDate] = useState(savedFilters.selectedNegativeFeedbackFromDate || '2026-01-01');
  const [selectedNegativeFeedbackToDate, setSelectedNegativeFeedbackToDate] = useState(savedFilters.selectedNegativeFeedbackToDate || '2026-01-09');
  const [selectedNegativeFeedbackYear, setSelectedNegativeFeedbackYear] = useState(savedFilters.selectedNegativeFeedbackYear || '2026');
  const [selectedNegativeFeedbackGroup, setSelectedNegativeFeedbackGroup] = useState(savedFilters.selectedNegativeFeedbackGroup || null);
  const [negativeFeedbackFilterType, setNegativeFeedbackFilterType] = useState(savedFilters.negativeFeedbackFilterType || 'month');
  const [showNegativeFeedbackGroupDropdown, setShowNegativeFeedbackGroupDropdown] = useState(false);

  // Data states
  const [assignmentGroupData, setAssignmentGroupData] = useState([]);
  const [teamScreenTimeData, setTeamScreenTimeData] = useState([]);
  const [allTeamsData, setAllTeamsData] = useState([]);
  const [positiveFeedbackData, setPositiveFeedbackData] = useState([]);
  const [negativeFeedbackData, setNegativeFeedbackData] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableSBUs, setAvailableSBUs] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [selectedSBU, setSelectedSBU] = useState([]);
  const [showSBUDropdown, setShowSBUDropdown] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState([]);
  const [showTrackDropdown, setShowTrackDropdown] = useState(false);
  
  // Backend data state
  const [ticketsData, setTicketsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiFeedbackData, setAiFeedbackData] = useState([]);
  const [sessionData, setSessionData] = useState([]);

  // Month and week options
  const monthOptions = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  const weekOptions = [
    { value: '1', label: 'Week 1' },
    { value: '2', label: 'Week 2' },
    { value: '3', label: 'Week 3' },
    { value: '4', label: 'Week 4' }
  ];

  const yearOptions = [
    { value: '2025', label: '2025' },
    { value: '2026', label: '2026' }
  ];

  // Ref for dropdown to handle click outside
  const groupDropdownRef = useRef(null);
  const positiveFeedbackGroupDropdownRef = useRef(null);
  const negativeFeedbackGroupDropdownRef = useRef(null);
  const trackDropdownRef = useRef(null);
  const sbuDropdownRef = useRef(null);

  // Group selection handlers
  const handleSelectAllGroups = () => {
    if (isAllGroupsSelected()) {
      setSelectedGroup([]);
    } else {
      setSelectedGroup(availableGroups.filter(g => g.value !== 'all'));
    }
  };

  const handleToggleGroup = (group) => {
    const currentGroups = Array.isArray(selectedGroup) ? selectedGroup : [];
    const isSelected = currentGroups.some(g => g.value === group.value);
    
    if (isSelected) {
      setSelectedGroup(currentGroups.filter(g => g.value !== group.value));
    } else {
      setSelectedGroup([...currentGroups, group]);
    }
  };

  const isAllGroupsSelected = () => {
    const actualGroups = availableGroups.filter(g => g.value !== 'all');
    const currentGroups = Array.isArray(selectedGroup) ? selectedGroup : [];
    return actualGroups.length > 0 && currentGroups.length === actualGroups.length;
  };

  const getSelectedGroupLabel = () => {
    if (!selectedGroup || selectedGroup.length === 0) return 'Select Groups...';
    // Check if "All Groups" option is selected
    if (selectedGroup.length === 1 && selectedGroup[0].value === 'all') return 'All Groups';
    if (isAllGroupsSelected()) return 'All Groups';
    return `${selectedGroup.length} Group${selectedGroup.length > 1 ? 's' : ''} Selected`;
  };

  // Positive Feedback group selection handlers
  const handleSelectAllPositiveFeedbackGroups = () => {
    if (isAllPositiveFeedbackGroupsSelected()) {
      setSelectedPositiveFeedbackGroup([]);
    } else {
      setSelectedPositiveFeedbackGroup(availableGroups.filter(g => g.value !== 'all'));
    }
  };

  const handleTogglePositiveFeedbackGroup = (group) => {
    const currentGroups = Array.isArray(selectedPositiveFeedbackGroup) ? selectedPositiveFeedbackGroup : [];
    const isSelected = currentGroups.some(g => g.value === group.value);
    
    if (isSelected) {
      setSelectedPositiveFeedbackGroup(currentGroups.filter(g => g.value !== group.value));
    } else {
      setSelectedPositiveFeedbackGroup([...currentGroups, group]);
    }
  };

  const isAllPositiveFeedbackGroupsSelected = () => {
    const actualGroups = availableGroups.filter(g => g.value !== 'all');
    const currentGroups = Array.isArray(selectedPositiveFeedbackGroup) ? selectedPositiveFeedbackGroup : [];
    return actualGroups.length > 0 && currentGroups.length === actualGroups.length;
  };

  const getSelectedPositiveFeedbackGroupLabel = () => {
    if (!selectedPositiveFeedbackGroup || selectedPositiveFeedbackGroup.length === 0) return 'Select Groups...';
    const actualGroupsCount = availableGroups.filter(g => g.value !== 'all').length;
    if (selectedPositiveFeedbackGroup.length === actualGroupsCount && actualGroupsCount > 0) return 'All Groups';
    return `${selectedPositiveFeedbackGroup.length} Group${selectedPositiveFeedbackGroup.length > 1 ? 's' : ''} Selected`;
  };

  // Negative Feedback group selection handlers
  const handleSelectAllNegativeFeedbackGroups = () => {
    if (isAllNegativeFeedbackGroupsSelected()) {
      setSelectedNegativeFeedbackGroup([]);
    } else {
      setSelectedNegativeFeedbackGroup(availableGroups.filter(g => g.value !== 'all'));
    }
  };

  const handleToggleNegativeFeedbackGroup = (group) => {
    const currentGroups = Array.isArray(selectedNegativeFeedbackGroup) ? selectedNegativeFeedbackGroup : [];
    const isSelected = currentGroups.some(g => g.value === group.value);
    
    if (isSelected) {
      setSelectedNegativeFeedbackGroup(currentGroups.filter(g => g.value !== group.value));
    } else {
      setSelectedNegativeFeedbackGroup([...currentGroups, group]);
    }
  };

  const isAllNegativeFeedbackGroupsSelected = () => {
    const actualGroups = availableGroups.filter(g => g.value !== 'all');
    const currentGroups = Array.isArray(selectedNegativeFeedbackGroup) ? selectedNegativeFeedbackGroup : [];
    return actualGroups.length > 0 && currentGroups.length === actualGroups.length;
  };

  const getSelectedNegativeFeedbackGroupLabel = () => {
    if (!selectedNegativeFeedbackGroup || selectedNegativeFeedbackGroup.length === 0) return 'Select Groups...';
    const actualGroupsCount = availableGroups.filter(g => g.value !== 'all').length;
    if (selectedNegativeFeedbackGroup.length === actualGroupsCount && actualGroupsCount > 0) return 'All Groups';
    return `${selectedNegativeFeedbackGroup.length} Group${selectedNegativeFeedbackGroup.length > 1 ? 's' : ''} Selected`;
  };

  // Track selection handlers
  const handleSelectAllTracks = () => {
    if (isAllTracksSelected()) {
      setSelectedTrack([]);
    } else {
      setSelectedTrack(availableTracks.filter(t => t.value !== 'all'));
    }
  };

  const handleToggleTrack = (track) => {
    const currentTracks = Array.isArray(selectedTrack) ? selectedTrack : [];
    const isSelected = currentTracks.some(t => t.value === track.value);
    
    if (isSelected) {
      setSelectedTrack(currentTracks.filter(t => t.value !== track.value));
    } else {
      setSelectedTrack([...currentTracks, track]);
    }
  };

  const isAllTracksSelected = () => {
    const actualTracks = availableTracks.filter(t => t.value !== 'all');
    const currentTracks = Array.isArray(selectedTrack) ? selectedTrack : [];
    return actualTracks.length > 0 && currentTracks.length === actualTracks.length;
  };

  const getSelectedTrackLabel = () => {
    if (!selectedTrack || selectedTrack.length === 0) return 'Select Tracks...';
    if (isAllTracksSelected()) return 'All Tracks';
    return `${selectedTrack.length} Track${selectedTrack.length > 1 ? 's' : ''} Selected`;
  };

  // SBU selection handlers
  const handleSelectAllSBU = () => {
    if (isAllSBUSelected()) {
      setSelectedSBU([]);
    } else {
      setSelectedSBU(availableSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleSBU = (sbu) => {
    const currentSBUs = Array.isArray(selectedSBU) ? selectedSBU : [];
    const isSelected = currentSBUs.some(s => s.value === sbu.value);
    
    if (isSelected) {
      setSelectedSBU(currentSBUs.filter(s => s.value !== sbu.value));
    } else {
      setSelectedSBU([...currentSBUs, sbu]);
    }
  };

  const isAllSBUSelected = () => {
    const actualSBUs = availableSBUs.filter(s => s.value !== 'all');
    const currentSBUs = Array.isArray(selectedSBU) ? selectedSBU : [];
    return actualSBUs.length > 0 && currentSBUs.length === actualSBUs.length;
  };

  const getSelectedSBULabel = () => {
    if (!selectedSBU || selectedSBU.length === 0) return 'Select SBU...';
    if (isAllSBUSelected()) return 'All SBU';
    return `${selectedSBU.length} SBU Selected`;
  };

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filtersToSave = {
      selectedGroupMonth,
      selectedGroupWeek,
      selectedGroupFromDate,
      selectedGroupToDate,
      selectedGroup,
      groupFilterType,
      selectedTeamMonth,
      selectedTeamWeek,
      selectedTeamFromDate,
      selectedTeamToDate,
      teamFilterType,
      selectedAllTeamsMonth,
      selectedAllTeamsWeek,
      selectedAllTeamsFromDate,
      selectedAllTeamsToDate,
      allTeamsFilterType,
      selectedPositiveFeedbackMonth,
      selectedPositiveFeedbackWeek,
      selectedPositiveFeedbackFromDate,
      selectedPositiveFeedbackToDate,
      selectedPositiveFeedbackGroup,
      positiveFeedbackFilterType,
      selectedNegativeFeedbackMonth,
      selectedNegativeFeedbackWeek,
      selectedNegativeFeedbackFromDate,
      selectedNegativeFeedbackToDate,
      selectedNegativeFeedbackGroup,
      negativeFeedbackFilterType,
      selectedSBU
    };
    
    try {
      localStorage.setItem('screenTimeFilters', JSON.stringify(filtersToSave));
    } catch (err) {
      console.error('Error saving filters:', err);
    }
  }, [
    selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroup, groupFilterType,
    selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, teamFilterType,
    selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, allTeamsFilterType,
    selectedPositiveFeedbackMonth, selectedPositiveFeedbackWeek, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackGroup, positiveFeedbackFilterType,
    selectedNegativeFeedbackMonth, selectedNegativeFeedbackWeek, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackGroup, negativeFeedbackFilterType,
    selectedSBU
  ]);

  // Fetch AI feedback and session data on mount
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        // Fetch AI feedback data
        const feedbackResponse = await fetch('/api/feedback-all');
        if (feedbackResponse.ok) {
          const feedbackData = await feedbackResponse.json();
          if (feedbackData.success) {
            console.log('AI Feedback Data Loaded:', feedbackData.feedback.length, 'entries');
            console.log('Thumbs Up:', feedbackData.feedback.filter(f => f.feedbackType === 'thumbs_up').length);
            console.log('Thumbs Down:', feedbackData.feedback.filter(f => f.feedbackType === 'thumbs_down').length);
            setAiFeedbackData(feedbackData.feedback);
          }
        }
        
        // Fetch session data with timestamps for filtering
        const sessionResponse = await fetch('/api/sessions/all');
        if (sessionResponse.ok) {
          const sessionDataResult = await sessionResponse.json();
          if (sessionDataResult.success) {
            console.log('Session Data Loaded:', sessionDataResult.data.length, 'sessions');
            setSessionData(sessionDataResult.data);
          }
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      }
    };
    
    fetchAnalyticsData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
      if (positiveFeedbackGroupDropdownRef.current && !positiveFeedbackGroupDropdownRef.current.contains(event.target)) {
        setShowPositiveFeedbackGroupDropdown(false);
      }
      if (negativeFeedbackGroupDropdownRef.current && !negativeFeedbackGroupDropdownRef.current.contains(event.target)) {
        setShowNegativeFeedbackGroupDropdown(false);
      }
      if (trackDropdownRef.current && !trackDropdownRef.current.contains(event.target)) {
        setShowTrackDropdown(false);
      }
      if (sbuDropdownRef.current && !sbuDropdownRef.current.contains(event.target)) {
        setShowSBUDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch data from backend
  const fetchData = async () => {
    const API_URL = "/api/tickets";
    const response = await fetch(API_URL, { method: "GET" });
    return response;
  };

  // Calculate screen time from ticket data (time from opened to resolved/closed)
  const calculateScreenTime = (ticket) => {
    if (!ticket.opened_at) return 0;
    
    const openedDate = new Date(ticket.opened_at);
    let endDate;
    
    // Use resolved_at if available, otherwise use updated_at or current time
    if (ticket.resolved_at) {
      endDate = new Date(ticket.resolved_at);
    } else if (ticket.updated_at) {
      endDate = new Date(ticket.updated_at);
    } else {
      endDate = new Date();
    }
    
    // Calculate hours difference
    const hoursDiff = (endDate - openedDate) / (1000 * 60 * 60);
    return Math.max(0, hoursDiff); // Ensure non-negative
  };

  // Filter tickets based on date/week/month
  const filterTicketsByDate = (tickets, filterType, month, week, fromDate, toDate) => {
    if (!tickets || tickets.length === 0) return [];
    
    return tickets.filter(ticket => {
      const ticketDate = new Date(ticket.opened_at || ticket.receivedOn);
      if (!ticket.opened_at && !ticket.receivedOn) return false;
      
      if (filterType === 'date') {
        // Filter by date range
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return ticketDate >= from && ticketDate <= to;
      } else if (filterType === 'week') {
        // Filter by week number
        const weekNum = parseInt(week);
        const startOfYear = new Date(ticketDate.getFullYear(), 0, 1);
        const daysSinceStart = Math.floor((ticketDate - startOfYear) / (1000 * 60 * 60 * 24));
        const ticketWeek = Math.ceil((daysSinceStart + startOfYear.getDay() + 1) / 7);
        return ticketWeek === weekNum;
      } else if (filterType === 'month') {
        // Filter by month
        const monthIndex = monthOptions.findIndex(m => m.value === month);
        return ticketDate.getMonth() === monthIndex;
      }
      return true;
    });
  };

  // Generate positive feedback data (Satisfied/Not Satisfied)
  const generatePositiveFeedbackData = (filterType, month, fromDate, toDate, year) => {
    // Filter AI feedback data by timestamp
    let filteredFeedback = aiFeedbackData;
    
    if (filterType === 'month' && month && year) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return feedbackDate.getFullYear() === parseInt(year) && feedbackDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }
    
    // Use filtered data from AI feedback table
    const satisfied = filteredFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
    const notSatisfied = filteredFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
    
    const feedbackData = [
      { name: 'Satisfied', count: satisfied },
      { name: 'Not Satisfied', count: notSatisfied }
    ];
    
    return feedbackData;
  };

  // Generate negative feedback data (Areas to be improved)
  const generateNegativeFeedbackData = (filterType, month, fromDate, toDate, year) => {
    // Filter AI feedback data by timestamp
    let filteredFeedback = aiFeedbackData;
    
    if (filterType === 'month' && month && year) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return feedbackDate.getFullYear() === parseInt(year) && feedbackDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }
    
    // Use filtered data from AI feedback table - group by reason
    const reasonCounts = {};
    
    filteredFeedback
      .filter(f => f.feedbackType === 'thumbs_down' && f.reason)
      .forEach(f => {
        const reason = f.reason;
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });
    
    // Convert to array format for chart
    const improvementAreas = Object.keys(reasonCounts).map(reason => ({
      name: reason,
      count: reasonCounts[reason]
    }));
    
    // Sort by count descending
    improvementAreas.sort((a, b) => b.count - a.count);
    
    return improvementAreas;
  };

  const generateAssignmentGroupData = (activeFilterType, group, month, week, fromDate, toDate, year) => {
    // Use session data instead of ticket data
    if (!sessionData || sessionData.length === 0) return [];
    
    // Check if "All Groups" is selected or if it's an array of groups
    const currentGroups = Array.isArray(group) ? group : [];
    const isAllGroups = currentGroups.length === 0 || currentGroups.some(g => g.value === 'all');
    const selectedGroups = currentGroups.filter(g => g.value !== 'all');
    
    // Filter sessions by selected assignment groups
    let filteredSessions = sessionData;
    
    if (!isAllGroups && selectedGroups.length > 0) {
      filteredSessions = sessionData.filter(session => {
        const sessionGroup = session.assignmentGroup || '';
        return selectedGroups.some(g => sessionGroup === g.value);
      });
    }
    
    // Filter by date/month/year
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }
    
    console.log('Filtered sessions for Assignment Group:', filteredSessions.length);
    
    // Group by user and sum their screen time
    const memberScreenTime = {};
    const userGroups = {};
    
    filteredSessions.forEach(session => {
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      const assignmentGroup = session.assignmentGroup || 'Unassigned';
      const duration = session.totalDuration || 0;
      
      if (!memberScreenTime[userName]) {
        memberScreenTime[userName] = 0;
        userGroups[userName] = assignmentGroup;
      }
      
      memberScreenTime[userName] += duration;
    });
    
    // Convert to chart format
    const chartData = Object.keys(memberScreenTime).map(userName => ({
      name: userName,
      screenTime: Math.round(memberScreenTime[userName] / 60), // Convert seconds to minutes
      team: userGroups[userName]
    }));
    
    // Sort by screen time descending and take top 10
    chartData.sort((a, b) => b.screenTime - a.screenTime);
    console.log('Assignment Group Chart Data:', chartData);
    return chartData.slice(0, 10);
  };

  const generateTeamScreenTimeData = (activeFilterType, month, week, fromDate, toDate, year) => {
    // Use session data for real-time screen time instead of ticket data
    if (!sessionData || sessionData.length === 0) {
      return [];
    }
    
    // Filter sessions by date/month/year
    let filteredSessions = sessionData;
    
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = new Date(session.startTime || session.timestamp);
        if (!session.startTime && !session.timestamp) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = new Date(session.startTime || session.timestamp);
        if (!session.startTime && !session.timestamp) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }
    
    // Group sessions by assignment group and calculate total screen time
    const teamScreenTime = {};
    
    filteredSessions.forEach(session => {
      const team = session.assignmentGroup || 'Unassigned';
      if (!team || team === 'Unassigned') return;
      
      // totalDuration is in seconds, convert to hours
      const screenTimeHours = (session.totalDuration || 0) / 3600;
      
      if (!teamScreenTime[team]) {
        teamScreenTime[team] = 0;
      }
      teamScreenTime[team] += screenTimeHours;
    });
    
    // Convert to array format for chart
    const result = Object.entries(teamScreenTime)
      .map(([name, screenTime]) => ({
        name: name,
        screenTime: parseFloat(screenTime.toFixed(1))
      }))
      .sort((a, b) => b.screenTime - a.screenTime)
      .slice(0, 10); // Top 10 teams
    
    return result;
  };

  // Generate all teams data showing assignment groups filtered by selected track
  const generateAllTeamsData = (activeFilterType, month, week, fromDate, toDate, tracks, sbus, year) => {
    // Use session data instead of ticket data for real-time user screen time
    if (!sessionData || sessionData.length === 0) {
      return [];
    }
    
    // Filter sessions by date/month/year
    let filteredSessions = sessionData;
    
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }

    // Determine if we need to filter by SBU and/or Track
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Group sessions by assignment group and calculate screen time
    const assignmentGroupData = {};
    
    filteredSessions.forEach(session => {
      const assignmentGroup = session.assignmentGroup;
      
      if (!assignmentGroup) {
        return; // Skip sessions without assignment group
      }
      
      // Get mapping for this assignment group
      const mapping = getGroupMapping(assignmentGroup);
      
      // Filter by SBU if selected
      if (selectedSBUValues.length > 0 && !selectedSBUValues.includes(mapping.sbu)) {
        return;
      }
      
      // Filter by Track if selected
      if (selectedTrackValues.length > 0 && !selectedTrackValues.includes(mapping.track)) {
        return;
      }
      
      // Use session duration (in seconds, convert to hours)
      const durationInSeconds = session.totalDuration || 0;
      const screenTimeHours = durationInSeconds / 3600;
      
      if (!assignmentGroupData[assignmentGroup]) {
        assignmentGroupData[assignmentGroup] = {
          name: assignmentGroup,
          screenTime: 0,
          sbu: mapping.sbu,
          track: mapping.track
        };
      }
      
      assignmentGroupData[assignmentGroup].screenTime += screenTimeHours;
    });
    
    // Convert to array format for chart
    const result = Object.values(assignmentGroupData)
      .map(group => ({
        name: group.name,
        screenTime: parseFloat(group.screenTime.toFixed(1)),
        sbu: group.sbu,
        track: group.track,
        displayName: `${group.name} (${group.sbu} - ${group.track})`
      }))
      .sort((a, b) => b.screenTime - a.screenTime)
      .slice(0, 10); // Top 10 assignment groups by screen time
    
    console.log('SBU/Track Chart Data (from sessions):', result);
    return result;
  };

  // Load Track Mapping - Demo Data
  useEffect(() => {
    const loadTrackMapping = () => {
      // Load SBUs from mapping
      const sbus = getAllSBUs();
      const sbuOptions = [
        { value: 'all', label: 'All SBUs' },
        ...sbus.map(sbu => ({ value: sbu, label: sbu }))
      ];
      setAvailableSBUs(sbuOptions);
      // Start with all SBUs selected (excluding the 'all' option itself)
      setSelectedSBU(sbus.map(sbu => ({ value: sbu, label: sbu })));
      
      // Load Tracks from mapping
      const tracks = getAllTracks();
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
      // Start with all tracks selected (excluding the 'all' option itself)
      setSelectedTrack(tracks.map(track => ({ value: track, label: track })));
    };
    
    loadTrackMapping();
  }, []);

  // Load tickets from backend on mount
  useEffect(() => {
    const loadTicketsData = async () => {
      // Try to load from cache first (faster)
      const cachedData = localStorage.getItem('agentAssistTicketsCache');
      if (cachedData) {
        try {
          const cache = JSON.parse(cachedData);
          const tickets = cache.tickets || cache;
          // Ensure tickets is an array and has data
          if (Array.isArray(tickets) && tickets.length > 0) {
            setTicketsData(tickets);
            console.log('Loaded', tickets.length, 'tickets from cache');
            return; // Use cache, don't fetch
          }
        } catch (cacheErr) {
          console.error('Error loading cached data:', cacheErr);
        }
      }
      
      // No cache or cache is empty, fetch from API
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetchData();
        const result = await response.json();
        const tickets = result.result || [];
        
        setTicketsData(tickets);
        
        // Cache the data
        const cacheData = {
          tickets: tickets,
          timestamp: Date.now()
        };
        localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load ticket data');
      } finally {
        setLoading(false);
      }
    };
    
    loadTicketsData();
  }, []);
  
  // Function to manually refresh data
  const handleSyncData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData();
      const result = await response.json();
      const tickets = result.result || [];
      
      setTicketsData(tickets);
      
      // Cache the data
      const cacheData = {
        tickets: tickets,
        timestamp: Date.now()
      };
      localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to sync data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize available groups from tickets data
  useEffect(() => {
    if (!ticketsData || ticketsData.length === 0) return;
    
    // Extract unique assignment groups from tickets
    const uniqueGroups = [...new Set(ticketsData
      .map(ticket => ticket.assignment_group || ticket.assignmentGroup)
      .filter(group => group !== null && group !== undefined && group !== '')
    )];
    
    // Create group options with "All Groups" as first option
    const groupOptions = [
      { value: 'all', label: 'All Groups' },
      ...uniqueGroups.map(group => ({
        value: group,
        label: group
      })).sort((a, b) => a.label.localeCompare(b.label))
    ];
    
    setAvailableGroups(groupOptions);
    
    // Set default for Assignment Group filter to "All Groups" (only once when groups are first loaded)
    if (groupOptions.length > 0 && availableGroups.length === 0) {
      // First time loading groups, set default to all individual groups (not the "All Groups" option)
      setSelectedGroup(groupOptions.filter(g => g.value !== 'all')); // All individual groups
      
      // For feedback filters, select all individual groups (not "All Groups")
      if (!selectedPositiveFeedbackGroup || selectedPositiveFeedbackGroup.length === 0) {
        setSelectedPositiveFeedbackGroup(groupOptions.filter(g => g.value !== 'all'));
      }
      if (!selectedNegativeFeedbackGroup || selectedNegativeFeedbackGroup.length === 0) {
        setSelectedNegativeFeedbackGroup(groupOptions.filter(g => g.value !== 'all'));
      }
    }
  }, [ticketsData, availableGroups.length]);

  // Filter available tracks based on selected SBU
  useEffect(() => {
    if (availableSBUs.length === 0) return;
    
    // If SBU is selected, filter tracks to only show those in selected SBUs
    if (selectedSBU && selectedSBU.length > 0) {
      const selectedSBUValues = selectedSBU.map(s => s.value);
      // Get all tracks from the mapping that belong to selected SBUs
      const tracksInSelectedSBUs = new Set();
      Object.values(GROUP_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSelectedSBUs.add(mapping.track);
        }
      });
      
      // Filter available tracks
      const allTracks = getAllTracks();
      const filteredTracks = allTracks.filter(track => tracksInSelectedSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
      
      // Clear selected tracks if they're not in the filtered list
      if (selectedTrack && selectedTrack.length > 0) {
        const validTracks = selectedTrack.filter(t => 
          filteredTracks.includes(t.value) || t.value === 'all'
        );
        if (validTracks.length !== selectedTrack.length) {
          setSelectedTrack(validTracks);
        }
      }
    } else {
      // No SBU selected, show all tracks
      const tracks = getAllTracks();
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
    }
  }, [selectedSBU, availableSBUs.length]);

  // Update data when filters or AI feedback data changes
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      setPositiveFeedbackData(generatePositiveFeedbackData(positiveFeedbackFilterType, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear));
    }
  }, [aiFeedbackData, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, selectedPositiveFeedbackGroup, positiveFeedbackFilterType]);

  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      setNegativeFeedbackData(generateNegativeFeedbackData(negativeFeedbackFilterType, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear));
    }
  }, [aiFeedbackData, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, selectedNegativeFeedbackGroup, negativeFeedbackFilterType]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setAssignmentGroupData(generateAssignmentGroupData(groupFilterType, selectedGroup, selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroupYear));
    }
  }, [sessionData, selectedGroup, selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroupYear, groupFilterType]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setTeamScreenTimeData(generateTeamScreenTimeData(teamFilterType, selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, selectedTeamYear));
    }
  }, [selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, selectedTeamYear, teamFilterType, sessionData]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setAllTeamsData(generateAllTeamsData(allTeamsFilterType, selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, selectedTrack, selectedSBU, selectedAllTeamsYear));
    }
  }, [selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, selectedAllTeamsYear, allTeamsFilterType, sessionData, selectedTrack, selectedSBU, availableTracks, availableSBUs]);

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderRadius: '6px',
      borderColor: '#e5e7eb',
      '&:hover': {
        borderColor: '#7B3FF2'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#7B3FF2' : state.isFocused ? '#f3f4f6' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:hover': {
        backgroundColor: state.isSelected ? '#7B3FF2' : '#f3f4f6'
      }
    })
  };

  const formatScreenTime = (hours) => {
    if (hours >= 100) {
      return `${hours.toFixed(0)} hrs`;
    }
    return `${hours.toFixed(1)} hrs`;
  };

  // Function to download data as Excel (CSV format)
  const downloadExcel = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    // Convert data to CSV format
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(val => 
        typeof val === 'string' && val.includes(',') ? `"${val}"` : val
      ).join(',')
    );
    const csv = [headers, ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPositiveFeedback = () => {
    const downloadData = positiveFeedbackData.map(item => ({
      'Assignment Group': item.name,
      'Positive Feedback Count': item.count
    }));
    const groupName = Array.isArray(selectedPositiveFeedbackGroup) && selectedPositiveFeedbackGroup.some(g => g.value === 'all') ? 'All_Groups' : (Array.isArray(selectedPositiveFeedbackGroup) ? selectedPositiveFeedbackGroup.map(g => g.label).join('_') : 'Group');
    const filename = `Positive_Feedback_${groupName}_${selectedPositiveFeedbackMonth}_Week${selectedPositiveFeedbackWeek}_${selectedPositiveFeedbackFromDate}_to_${selectedPositiveFeedbackToDate}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadNegativeFeedback = () => {
    const downloadData = negativeFeedbackData.map(item => ({
      'Assignment Group': item.name,
      'Negative Feedback Count': item.count
    }));
    const groupName = Array.isArray(selectedNegativeFeedbackGroup) && selectedNegativeFeedbackGroup.some(g => g.value === 'all') ? 'All_Groups' : (Array.isArray(selectedNegativeFeedbackGroup) ? selectedNegativeFeedbackGroup.map(g => g.label).join('_') : 'Group');
    const filename = `Negative_Feedback_${groupName}_${selectedNegativeFeedbackMonth}_Week${selectedNegativeFeedbackWeek}_${selectedNegativeFeedbackFromDate}_to_${selectedNegativeFeedbackToDate}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadGroupData = () => {
    const isAllGroups = Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === 'all');
    const downloadData = assignmentGroupData.map(item => ({
      'User Name': item.name,
      ...(isAllGroups && { 'Team': item.team }),
      'Screen Time (hours)': item.screenTime
    }));
    const groupName = isAllGroups ? 'All_Groups' : (Array.isArray(selectedGroup) ? selectedGroup.map(g => g.label).join('_') : selectedGroup?.label || 'Group');
    const filename = `Assignment_Group_${groupName}_${selectedGroupMonth}_Week${selectedGroupWeek}_${selectedGroupFromDate}_to_${selectedGroupToDate}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTeamData = () => {
    const filename = `Team_Screen_Time_${selectedTeamMonth}_Week${selectedTeamWeek}_${selectedTeamFromDate}_to_${selectedTeamToDate}`;
    downloadExcel(teamScreenTimeData, filename);
  };

  const handleDownloadAllTeamsData = () => {
    const downloadData = allTeamsData.map(item => ({
      'Team Name': item.name,
      'Screen Time (hours)': item.screenTime,
      'Assignment Groups Count': item.groupCount,
      'Assignment Groups': item.groupsText
    }));
    const filename = `All_Teams_Combined_${selectedAllTeamsMonth}_Week${selectedAllTeamsWeek}_${selectedAllTeamsFromDate}_to_${selectedAllTeamsToDate}`;
    downloadExcel(downloadData, filename);
  };

  return (
    <div className="analytics-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} />
      
      <div className="dashboard-content screen-time-page">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">
              <span className="card-title-icon">
                <FaClock className="header-icon-purple" />
                <span>User Analytics</span>
              </span>
            </h1>
            <p className="dashboard-subtitle">Monitor user engagement and ticket resolution time across different views (Real-time Data)</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={handleSyncData} 
              className="back-link" 
              disabled={loading} 
              style={{ 
                background: loading ? '#ccc' : '#7B3FF2', 
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                border: 'none'
              }}
            >
              🔄 {loading ? 'Syncing...' : 'Sync Data'}
            </button>
            <Link to="/analytics" className="back-link" style={{ background: '#3b82f6', color: 'white' }}>
              📊 Analytics Dashboard
            </Link>
            <Link to="/AgentAssist" className="back-link">
              <FaArrowLeft className="back-arrow-icon" />
              <span>Back to Dashboard</span>
            </Link>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-title">Loading screen time data...</div>
            <div className="loading-subtitle">Please wait while we fetch the latest information</div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Data Display */}
        {!loading && (
          <>
        {/* 4. Screen Time as per All Teams - Shows teams with their assignment groups combined */}
        <div className="analytics-card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-title-icon">
                <FaUsers className="header-icon-green" />
                <span>Screen Time by Assignment Groups (SBU and Track-wise)</span>
              </span>
            </div>
            <div className="filters-row">
              {/* SBU Selector with Checkbox - FIRST */}
              <div className="sbu-selector" ref={sbuDropdownRef} style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginRight: '16px',
                minWidth: '220px'
              }}>
                <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  SBU:
                </label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowSBUDropdown(!showSBUDropdown)}
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ 
                      fontSize: '14px', 
                      color: selectedSBU?.length > 0 ? '#1f2937' : '#9ca3af',
                      fontWeight: '500'
                    }}>
                      {getSelectedSBULabel()}
                    </span>
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      transform: showSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>▼</span>
                  </div>
                  
                  {showSBUDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '250px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      {/* Select All Option */}
                      <div 
                        onClick={handleSelectAllSBU}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          borderBottom: '2px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                          fontWeight: '600',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input
                          type="checkbox"
                          checked={isAllSBUSelected()}
                          onChange={handleSelectAllSBU}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#10b981'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span style={{ fontSize: '14px', color: '#1f2937' }}>Select All SBU</span>
                      </div>

                      {/* Individual SBU Options */}
                      {availableSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(selectedSBU) && selectedSBU.some(s => s.value === sbu.value);
                        return (
                          <div
                            key={sbu.value}
                            onClick={() => handleToggleSBU(sbu)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              backgroundColor: isSelected ? '#f0fdf4' : 'white',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSBU(sbu)}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#10b981'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#374151',
                              fontWeight: isSelected ? '500' : '400'
                            }}>
                              {sbu.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector with Checkbox - SECOND */}
              <div className="track-selector" ref={trackDropdownRef} style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginRight: '16px',
                minWidth: '250px'
              }}>
                <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Track:
                </label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTrackDropdown(!showTrackDropdown)}
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ 
                      fontSize: '14px', 
                      color: selectedTrack?.length > 0 ? '#1f2937' : '#9ca3af',
                      fontWeight: '500'
                    }}>
                      {getSelectedTrackLabel()}
                    </span>
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      transform: showTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>▼</span>
                  </div>
                  
                  {showTrackDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      {/* Select All Option */}
                      <div 
                        onClick={handleSelectAllTracks}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          borderBottom: '2px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                          fontWeight: '600',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input
                          type="checkbox"
                          checked={isAllTracksSelected()}
                          onChange={handleSelectAllTracks}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#10b981'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span style={{ fontSize: '14px', color: '#1f2937' }}>Select All Tracks</span>
                      </div>

                      {/* Individual Track Options */}
                      {availableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(selectedTrack) && selectedTrack.some(t => t.value === track.value);
                        return (
                          <div
                            key={track.value}
                            onClick={() => handleToggleTrack(track)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              backgroundColor: isSelected ? '#f0fdf4' : 'white',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleTrack(track)}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#10b981'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#374151',
                              fontWeight: isSelected ? '500' : '400'
                            }}>
                              {track.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Filter Type Selector */}
              <div className="filter-type-group">
                <button 
                  className={allTeamsFilterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
                  onClick={() => setAllTeamsFilterType('month')}
                >
                  Month
                </button>
                <button 
                  className={allTeamsFilterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
                  onClick={() => setAllTeamsFilterType('date')}
                >
                  Date
                </button>
              </div>
              
              {/* Conditional Filter Selectors */}
              {allTeamsFilterType === 'month' && (
                <div className="month-selector">
                  <label style={{ marginRight: '8px', fontSize: '14px', color: '#6b7280' }}>
                    Select Month:
                  </label>
                  <Select
                    value={monthOptions.find(m => m.value === selectedAllTeamsMonth)}
                    onChange={(option) => setSelectedAllTeamsMonth(option.value)}
                    options={monthOptions}
                    styles={customStyles}
                    className="month-select"
                  />
                  <label style={{ marginLeft: '12px', marginRight: '8px', fontSize: '14px', color: '#6b7280' }}>
                    Year:
                  </label>
                  <Select
                    value={yearOptions.find(y => y.value === selectedAllTeamsYear)}
                    onChange={(option) => setSelectedAllTeamsYear(option.value)}
                    options={yearOptions}
                    styles={customStyles}
                    className="year-select"
                  />
                </div>
              )}
              
              {allTeamsFilterType === 'date' && (
                <div className="date-selector">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>From:</label>
                      <input 
                        type="date" 
                        value={selectedAllTeamsFromDate}
                        onChange={(e) => setSelectedAllTeamsFromDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>To:</label>
                      <input 
                        type="date" 
                        value={selectedAllTeamsToDate}
                        onChange={(e) => setSelectedAllTeamsToDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={550}>
              <BarChart data={allTeamsData} margin={{ top: 20, right: 30, bottom: 120, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  stroke="#6b7280"
                  style={{ fontSize: '10px' }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  dy={20}
                  dx={-5}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [formatScreenTime(value), 'Screen Time']}
                  labelFormatter={(label) => label}
                />
                <Legend />
                <Bar 
                  dataKey="screenTime" 
                  fill="#10b981" 
                  radius={[8, 8, 0, 0]}
                  name="Screen Time (hrs)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="feedback-grid">
          {/* Dashboard 3A: Overall Feedback */}
          <div className="analytics-card">
            <div className="card-header">
              <div className="card-title">
                <span className="card-title-icon">
                  <span className="feedback-emoji">😊</span>
                  <span>Overall Feedback</span>
                </span>
              </div>
              <div className="filters-row filters-column">
                {/* Assignment Group Filter */}
                <div className="group-selector">
                  <label className="filter-label-nowrap">
                    Group:
                  </label>
                  <div ref={positiveFeedbackGroupDropdownRef} className="dropdown-container">
                    <div 
                      onClick={() => setShowPositiveFeedbackGroupDropdown(!showPositiveFeedbackGroupDropdown)}
                      className="dropdown-button green"
                    >
                      <span className={selectedPositiveFeedbackGroup?.length > 0 ? 'dropdown-label selected' : 'dropdown-label'}>
                        {getSelectedPositiveFeedbackGroupLabel()}
                      </span>
                      <span className={showPositiveFeedbackGroupDropdown ? 'dropdown-arrow open' : 'dropdown-arrow'}>▼</span>
                    </div>
                    
                    {showPositiveFeedbackGroupDropdown && (
                      <div className="dropdown-menu">
                        {/* Select All Option */}
                        <div 
                          onClick={handleSelectAllPositiveFeedbackGroups}
                          className="dropdown-select-all green"
                        >
                          <input 
                            type="checkbox" 
                            checked={isAllPositiveFeedbackGroupsSelected()}
                            onChange={handleSelectAllPositiveFeedbackGroups}
                            className="dropdown-checkbox green"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="dropdown-select-all-label">Select All Groups</span>
                        </div>
                        
                        {/* Individual Group Options */}
                        {availableGroups.filter(g => g.value !== 'all').map((group, index) => (
                          <div 
                            key={group.value}
                            onClick={() => handleTogglePositiveFeedbackGroup(group)}
                            className={Array.isArray(selectedPositiveFeedbackGroup) && selectedPositiveFeedbackGroup.some(g => g.value === group.value) ? 'dropdown-item selected green' : 'dropdown-item'}
                          >
                            <input 
                              type="checkbox" 
                              checked={Array.isArray(selectedPositiveFeedbackGroup) && selectedPositiveFeedbackGroup.some(g => g.value === group.value)}
                              onChange={() => handleTogglePositiveFeedbackGroup(group)}
                              className="dropdown-checkbox green"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className={Array.isArray(selectedPositiveFeedbackGroup) && selectedPositiveFeedbackGroup.some(g => g.value === group.value) ? 'dropdown-item-label selected' : 'dropdown-item-label'}>{group.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: '100%' }}>
                  {/* Filter Type */}
                  <div className="filter-type-group">
                    <button 
                      className={positiveFeedbackFilterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
                      onClick={() => setPositiveFeedbackFilterType('month')}
                    >
                      Month
                    </button>
                    <button 
                      className={positiveFeedbackFilterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
                      onClick={() => setPositiveFeedbackFilterType('date')}
                    >
                      Date
                    </button>
                  </div>
                  
                  {/* Conditional Filters */}
                  {positiveFeedbackFilterType === 'month' && (
                    <>
                      <Select
                        value={monthOptions.find(m => m.value === selectedPositiveFeedbackMonth)}
                        onChange={(option) => setSelectedPositiveFeedbackMonth(option.value)}
                        options={monthOptions}
                        styles={customStyles}
                        className="month-select"
                      />
                      <Select
                        value={yearOptions.find(y => y.value === selectedPositiveFeedbackYear)}
                        onChange={(option) => setSelectedPositiveFeedbackYear(option.value)}
                        options={yearOptions}
                        styles={customStyles}
                        className="year-select"
                        style={{ marginLeft: '8px' }}
                      />
                    </>
                  )}
                </div>

                {positiveFeedbackFilterType === 'date' && (
                  <div className="date-range-container" style={{ width: '100%', fontSize: '13px' }}>
                    <div className="date-group" style={{ flex: 1 }}>
                      <label className="filter-label-nowrap" style={{ fontSize: '13px' }}>From:</label>
                      <input 
                        type="date" 
                        value={selectedPositiveFeedbackFromDate}
                        onChange={(e) => setSelectedPositiveFeedbackFromDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-group" style={{ flex: 1 }}>
                      <label className="filter-label-nowrap" style={{ fontSize: '13px' }}>To:</label>
                      <input 
                        type="date" 
                        value={selectedPositiveFeedbackToDate}
                        onChange={(e) => setSelectedPositiveFeedbackToDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={positiveFeedbackData} margin={{ top: 20, right: 30, bottom: 100, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#6b7280"
                    style={{ fontSize: '10px' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    dy={20}
                    dx={-5}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    style={{ fontSize: '11px' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [value, 'Count']}
                  />
                  <Legend />
                  <Bar 
                    dataKey="count" 
                    fill="#10b981" 
                    radius={[8, 8, 0, 0]}
                    name="Satisfied Customers"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="download-button-container">
              <button onClick={handleDownloadPositiveFeedback} className="download-btn">
                📥 Download
              </button>
            </div>
          </div>

          {/* Dashboard 3B: Areas to be improved */}
          <div className="analytics-card">
            <div className="card-header">
              <div className="card-title">
                <span className="card-title-icon">
                  <span>Areas to be improved</span>
                </span>
              </div>
              <div className="filters-row filters-column">
                {/* Assignment Group Filter */}
                <div className="group-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <label style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                    Group:
                  </label>
                  <div ref={negativeFeedbackGroupDropdownRef} style={{ position: 'relative', flex: '1' }}>
                    <div 
                      onClick={() => setShowNegativeFeedbackGroupDropdown(!showNegativeFeedbackGroupDropdown)}
                      style={{
                        padding: '10px 14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#ef4444';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                      }}
                    >
                      <span style={{ 
                        fontSize: '14px', 
                        color: selectedNegativeFeedbackGroup?.length > 0 ? '#1f2937' : '#9ca3af',
                        fontWeight: '500'
                      }}>
                        {getSelectedNegativeFeedbackGroupLabel()}
                      </span>
                      <span style={{ 
                        fontSize: '10px', 
                        color: '#6b7280',
                        transform: showNegativeFeedbackGroupDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}>▼</span>
                    </div>
                    
                    {showNegativeFeedbackGroupDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                        maxHeight: '320px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        animation: 'slideDown 0.2s ease'
                      }}>
                        {/* Select All Option */}
                        <div 
                          onClick={handleSelectAllNegativeFeedbackGroups}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            borderBottom: '2px solid #e5e7eb',
                            backgroundColor: '#f9fafb',
                            fontWeight: '600',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        >
                          <input 
                            type="checkbox" 
                            checked={isAllNegativeFeedbackGroupsSelected()}
                            onChange={handleSelectAllNegativeFeedbackGroups}
                            style={{ 
                              width: '16px', 
                              height: '16px', 
                              cursor: 'pointer',
                              accentColor: '#ef4444'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span style={{ fontSize: '14px', color: '#1f2937' }}>Select All Groups</span>
                        </div>
                        
                        {/* Individual Group Options */}
                        {availableGroups.filter(g => g.value !== 'all').map((group, index) => (
                          <div 
                            key={group.value}
                            onClick={() => handleToggleNegativeFeedbackGroup(group)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              borderBottom: index < availableGroups.length - 2 ? '1px solid #f3f4f6' : 'none',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <input 
                              type="checkbox" 
                              checked={Array.isArray(selectedNegativeFeedbackGroup) && selectedNegativeFeedbackGroup.some(g => g.value === group.value)}
                              onChange={() => handleToggleNegativeFeedbackGroup(group)}
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                cursor: 'pointer',
                                accentColor: '#ef4444'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '14px', 
                              color: '#374151',
                              lineHeight: '1.4'
                            }}>{group.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: '100%' }}>
                  {/* Filter Type */}
                  <div className="filter-type-group">
                    <button 
                      className={negativeFeedbackFilterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
                      onClick={() => setNegativeFeedbackFilterType('month')}
                    >
                      Month
                    </button>
                    <button 
                      className={negativeFeedbackFilterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
                      onClick={() => setNegativeFeedbackFilterType('date')}
                    >
                      Date
                    </button>
                  </div>
                  
                  {/* Conditional Filters */}
                  {negativeFeedbackFilterType === 'month' && (
                    <>
                      <Select
                        value={monthOptions.find(m => m.value === selectedNegativeFeedbackMonth)}
                        onChange={(option) => setSelectedNegativeFeedbackMonth(option.value)}
                        options={monthOptions}
                        styles={customStyles}
                        className="month-select"
                      />
                      <Select
                        value={yearOptions.find(y => y.value === selectedNegativeFeedbackYear)}
                        onChange={(option) => setSelectedNegativeFeedbackYear(option.value)}
                        options={yearOptions}
                        styles={customStyles}
                        className="year-select"
                        style={{ marginLeft: '8px' }}
                      />
                    </>
                  )}
                </div>

                {negativeFeedbackFilterType === 'date' && (
                  <div className="date-range-container" style={{ width: '100%', fontSize: '13px' }}>
                    <div className="date-group" style={{ flex: 1 }}>
                      <label className="filter-label-nowrap" style={{ fontSize: '13px' }}>From:</label>
                      <input 
                        type="date" 
                        value={selectedNegativeFeedbackFromDate}
                        onChange={(e) => setSelectedNegativeFeedbackFromDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-group" style={{ flex: 1 }}>
                      <label className="filter-label-nowrap" style={{ fontSize: '13px' }}>To:</label>
                      <input 
                        type="date" 
                        value={selectedNegativeFeedbackToDate}
                        onChange={(e) => setSelectedNegativeFeedbackToDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={negativeFeedbackData} margin={{ top: 20, right: 30, bottom: 100, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#6b7280"
                    style={{ fontSize: '10px' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    dy={20}
                    dx={-5}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    style={{ fontSize: '11px' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [value, 'Count']}
                  />
                  <Legend />
                  <Bar 
                    dataKey="count" 
                    fill="#ef4444" 
                    radius={[8, 8, 0, 0]}
                    name="Not Satisfied"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="download-button-container">
              <button onClick={handleDownloadNegativeFeedback} className="download-btn">
                📥 Download
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard 2: Assignment Group Screen Time */}
        <div className="analytics-card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-title-icon">
                <FaUsers className="header-icon-blue" />
                <span>Assignment Group Screen Time</span>
              </span>
            </div>
            <div className="filters-row">
              <div className="group-selector">
                <label className="filter-label-nowrap">
                  Group:
                </label>
                <div ref={groupDropdownRef} className="dropdown-container-width">
                  <div 
                    onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                    className="dropdown-button"
                  >
                    <span className={selectedGroup?.length > 0 ? 'dropdown-label selected' : 'dropdown-label'}>
                      {getSelectedGroupLabel()}
                    </span>
                    <span className={showGroupDropdown ? 'dropdown-arrow open' : 'dropdown-arrow'}>▼</span>
                  </div>
                  
                  {showGroupDropdown && (
                    <div className="dropdown-menu">
                      {/* Select All Option */}
                      <div 
                        onClick={handleSelectAllGroups}
                        className="dropdown-select-all dropdown-select-all blue"
                      >
                        <input 
                          type="checkbox" 
                          checked={isAllGroupsSelected()}
                          onChange={handleSelectAllGroups}
                          className="dropdown-checkbox"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="dropdown-select-all-label">Select All Groups</span>
                      </div>
                      
                      {/* Individual Group Options */}
                      {availableGroups.filter(g => g.value !== 'all').map((group, index) => (
                        <div 
                          key={group.value}
                          onClick={() => handleToggleGroup(group)}
                          className={Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === group.value) ? 'dropdown-item selected' : 'dropdown-item'}
                        >
                          <input 
                            type="checkbox" 
                            checked={Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === group.value)}
                            onChange={() => handleToggleGroup(group)}
                            className="dropdown-checkbox"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className={Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === group.value) ? 'dropdown-item-label selected' : 'dropdown-item-label'}>{group.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Filter Type Selector */}
              <div className="filter-type-group">
                <button 
                  className={groupFilterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
                  onClick={() => setGroupFilterType('month')}
                >
                  Month
                </button>
                <button 
                  className={groupFilterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
                  onClick={() => setGroupFilterType('date')}
                >
                  Date
                </button>
              </div>
              
              {/* Conditional Filter Selectors */}
              {groupFilterType === 'month' && (
                <div className="month-selector">
                  <label className="filter-label">
                    Select Month:
                  </label>
                  <Select
                    value={monthOptions.find(m => m.value === selectedGroupMonth)}
                    onChange={(option) => setSelectedGroupMonth(option.value)}
                    options={monthOptions}
                    styles={customStyles}
                    className="month-select"
                  />
                  <label className="filter-label" style={{ marginLeft: '12px' }}>
                    Year:
                  </label>
                  <Select
                    value={yearOptions.find(y => y.value === selectedGroupYear)}
                    onChange={(option) => setSelectedGroupYear(option.value)}
                    options={yearOptions}
                    styles={customStyles}
                    className="year-select"
                  />
                </div>
              )}
              
              {groupFilterType === 'date' && (
                <div className="date-selector">
                  <div className="date-range-container">
                    <div className="date-group">
                      <label className="filter-label-nowrap">From:</label>
                      <input 
                        type="date" 
                        value={selectedGroupFromDate}
                        onChange={(e) => setSelectedGroupFromDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-group">
                      <label className="filter-label-nowrap">To:</label>
                      <input 
                        type="date" 
                        value={selectedGroupToDate}
                        onChange={(e) => setSelectedGroupToDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={500}>
              <BarChart data={assignmentGroupData} margin={{ top: 20, right: 30, bottom: 100, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name"
                  stroke="#6b7280"
                  style={{ fontSize: '10px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  dy={20}
                  dx={-5}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [`${value} min`, 'Screen Time']}
                />
                <Legend />
                <Bar 
                  dataKey="screenTime" 
                  fill="#3b82f6" 
                  radius={[8, 8, 0, 0]}
                  name="Screen Time (min)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="download-button-container">
            <button onClick={handleDownloadGroupData} className="download-btn">
              📥 Download
            </button>
          </div>
        </div>

        {/* Dashboard 1: Team-wise Screen Time */}
        <div className="analytics-card">
          <div className="card-header">
            <div className="card-title">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <FaUsers style={{ marginRight: '8px', color: '#10b981' }} />
                {/* <span>Team-wise Screen Time (by Assignment Group)</span> */}
                 <span>Top 10 screen time as per assignment groups</span>
              </span>
            </div>
            <div className="filters-row">
              {/* Filter Type Selector */}
              <div className="filter-type-group">
                <button 
                  className={teamFilterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
                  onClick={() => setTeamFilterType('month')}
                >
                  Month
                </button>
                <button 
                  className={teamFilterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
                  onClick={() => setTeamFilterType('date')}
                >
                  Date
                </button>
              </div>
              
              {/* Conditional Filter Selectors */}
              {teamFilterType === 'month' && (
                <div className="month-selector">
                  <label className="filter-label">
                    Select Month:
                  </label>
                  <Select
                    value={monthOptions.find(m => m.value === selectedTeamMonth)}
                    onChange={(option) => setSelectedTeamMonth(option.value)}
                    options={monthOptions}
                    styles={customStyles}
                    className="month-select"
                  />
                  <label className="filter-label" style={{ marginLeft: '12px' }}>
                    Year:
                  </label>
                  <Select
                    value={yearOptions.find(y => y.value === selectedTeamYear)}
                    onChange={(option) => setSelectedTeamYear(option.value)}
                    options={yearOptions}
                    styles={customStyles}
                    className="year-select"
                  />
                </div>
              )}
              
              {teamFilterType === 'date' && (
                <div className="date-selector">
                  <div className="date-range-container">
                    <div className="date-group">
                      <label className="filter-label-nowrap">From:</label>
                      <input 
                        type="date" 
                        value={selectedTeamFromDate}
                        onChange={(e) => setSelectedTeamFromDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-group">
                      <label className="filter-label-nowrap">To:</label>
                      <input 
                        type="date" 
                        value={selectedTeamToDate}
                        onChange={(e) => setSelectedTeamToDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={teamScreenTimeData} margin={{ top: 20, right: 30, bottom: 100, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  stroke="#6b7280"
                  style={{ fontSize: '10px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  dy={20}
                  dx={-5}
                />
                <YAxis 
                  stroke="#6b7280"
                  className="chart-xaxis"
                />
                <Tooltip 
                  wrapperClassName="custom-tooltip"
                  formatter={(value) => [formatScreenTime(value), 'Screen Time']}
                />
                <Legend />
                <Bar 
                  dataKey="screenTime" 
                  fill="#10b981" 
                  radius={[8, 8, 0, 0]}
                  name="Screen Time (hrs)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="download-button-container">
            <button onClick={handleDownloadTeamData} className="download-btn">
              📥 Download
            </button>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScreenTimeAnalytics;
